import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import localforage from 'localforage';

import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LoadingModal from '../../components/common/LoadingModal.jsx';
import styles from './main.module.css';
import { getUserProfile } from '../../services/api';

import {
    dictationLanguages,
    translationLanguages,
    transliterationLanguages,
    COMMANDS,
} from './constants/speechConstants';

import { useAlerts } from './utils/alertUtils';
import useSpeechRecognition from './hooks/useSpeechRecognition';
import useFontConversion from './hooks/useFontConversion';
import useTransliteration from './hooks/useTransliteration';
import useAIFeatures from './hooks/useAIFeatures';
import useEditorManagement from './hooks/useEditorManagement';
import useUserLicense from './hooks/useUserLicense';
import useFileConversion from './hooks/useFileConversion';

import SaveContentModal from './components/SaveContentModal';
import CommandsModal from './components/CommandsModal';
import DraftCategoriesModal from './components/DraftCategoriesModal';
import CategoryDetailsModal from './components/CategoryDetailsModal';
import TranslationModal from './components/TranslationModal';
import FontConversionModal from './components/FontConversionModal';
import AudioUploadModal from './components/AudioUploadModal';
import ImageUploadModal from './components/ImageUploadModal';
import PdfUploadModal from './components/PdfUploadModal';
import ConversionResultModal from './components/ConversionResultModal';
import InterimResultsDisplay from './components/InterimResultsDisplay';

function ModernSpeechEditor() {
    const { user, logoutUser } = useAuth();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [editorHtml, setEditorHtml] = useState('');
    const [dictationLang, setDictationLang] = useState('en-in');
    const [translateLang, setTranslateLang] = useState('en');
    const [transliterationLang, setTransliterationLang] = useState('hi');
    const [isTransliterationEnabled, setIsTransliterationEnabled] = useState(false);
    const [commandTab, setCommandTab] = useState('en');
    const [isVerifyingToken, setIsVerifyingToken] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [categoryDetailsModalVisible, setCategoryDetailsModalVisible] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const quillRef = useRef(null);
    const micButtonRef = useRef(null);

    const { error, successMessage, showSuccess, showError, clearAlerts } = useAlerts();

    // Define handleClear as a ref to avoid circular dependency
    const handleClearRef = useRef();

    const handleClearCallback = useCallback(() => {
        handleClearRef.current?.();
    }, []);

    const {
        listening,
        setListening,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        recognitionRef,
        startRecognition,
        stopRecognition,
        interimText,
    } = useSpeechRecognition(quillRef, dictationLang, showError, handleClearCallback);

    const handleClear = useCallback(async () => {
        clearAlerts();
        if (window.confirm("Are you sure you want to clear the editor?")) {
            quillRef.current?.getEditor().setText('');
            setEditorHtml('');
            stopRecognition();
            // Clear the saved content from localStorage
            try {
                await localforage.removeItem('editorContent');
            } catch (error) {
                console.error('Error clearing editor content from storage:', error);
            }
            showSuccess("Editor cleared.");
        }
    }, [clearAlerts, showSuccess, stopRecognition]);

    // Update the ref with the current handleClear function
    handleClearRef.current = handleClear;

    const {
        isConvertingFont,
        conversionOutput,
        fontConversionModalVisible,
        selectedFont,
        setFontConversionModalVisible,
        handleFontConversion,
    } = useFontConversion(quillRef, showError, clearAlerts);

    const {
        isTransliterating,
        handleTransliteration,
    } = useTransliteration(quillRef, transliterationLang, isTransliterationEnabled, showError);

    const {
        isTranslating,
        translatedText,
        translationModalVisible,
        setTranslationModalVisible,
        isFixingGrammar,
        isExpandingContent,
        isGeneratingDraft,
        isRefiningTone,
        draftModalVisible,
        setDraftModalVisible,
        isLoadingModalVisible,
        loadingMessage,
        handleFixGrammar,
        handleExpandContent,
        handleGenerateDraft,
        handleConfirmGenerateDraft,
        handleTranslate,
        handleRefineTone,
    } = useAIFeatures(quillRef, showError, showSuccess, clearAlerts);

    const {
        isSavingContent,
        saveModalVisible,
        setSaveModalVisible,
        contentTitle,
        setContentTitle,
        contentDescription,
        setContentDescription,
        handleCopy,
        handlePrint,
        handleSave,
        handleConfirmSave,
    } = useEditorManagement(quillRef, showError, showSuccess, clearAlerts);

    const { expiryDisplayText, actualExpiryDate } = useUserLicense(user);

    const {
        isConvertingAudio,
        isConvertingImage,
        isConvertingPdf,
        audioUploadModalVisible,
        imageUploadModalVisible,
        pdfUploadModalVisible,
        resultModalVisible,
        convertedText,
        resultType,
        isLoadingModalVisible: fileLoadingModalVisible,
        loadingMessage: fileLoadingMessage,
        setAudioUploadModalVisible,
        setImageUploadModalVisible,
        setPdfUploadModalVisible,
        setResultModalVisible,
        handleAudioToText,
        handleImageToText,
        handlePdfToText,
        handleInsertText,
        handleCopyText,
    } = useFileConversion(quillRef, showError, showSuccess, clearAlerts);

    const conversionResultConfig = {
        audio: {
            title: 'Audio Conversion Result',
            icon: 'fas fa-file-audio',
        },
        image: {
            title: 'Image Text Extraction Result',
            icon: 'fas fa-file-image',
        },
        pdf: {
            title: 'PDF Text Extraction Result',
            icon: 'fas fa-file-pdf',
        },
    };

    const activeConversionResult = conversionResultConfig[resultType] || conversionResultConfig.image;

    // Load editor content from localStorage on component mount
    useEffect(() => {
        const loadEditorContent = async () => {
            try {
                // Check if there's imported content from navigation state first
                if (state?.importedHtml) {
                    setEditorHtml(state.importedHtml);
                    // Save the imported content to localStorage for persistence
                    await localforage.setItem('editorContent', state.importedHtml);
                    return;
                }

                // Otherwise, load from localStorage
                const savedContent = await localforage.getItem('editorContent');
                if (savedContent) {
                    setEditorHtml(savedContent);
                }
            } catch (error) {
                console.error('Error loading editor content:', error);
            }
        };

        loadEditorContent();
    }, [state?.importedHtml]);

    // Auto-save editor content to localStorage whenever it changes
    useEffect(() => {
        const saveEditorContent = async () => {
            try {
                if (editorHtml) {
                    await localforage.setItem('editorContent', editorHtml);
                }
            } catch (error) {
                console.error('Error saving editor content:', error);
            }
        };

        // Debounce the save operation to avoid excessive writes
        const timeoutId = setTimeout(saveEditorContent, 500);
        return () => clearTimeout(timeoutId);
    }, [editorHtml]);

    const handleEditorChange = (content, delta, source, editor) => {
        setEditorHtml(content);
        if (source === 'user' && isTransliterationEnabled) {
            const lastOp = delta.ops[delta.ops.length - 1];
            if (lastOp && lastOp.insert === ' ') {
                const range = editor.getSelection();
                if (range) {
                    const textBeforeCursor = editor.getText(0, range.index - 1);
                    const words = textBeforeCursor.trim().split(/\s+/);
                    const lastWord = words.pop();
                    if (lastWord) {
                        handleTransliteration(lastWord);
                    }
                }
            }
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'font': [] }, { 'size': [] }], ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }], [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'header': '1' }, { 'header': '2' }, 'blockquote', 'code-block'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }, { 'align': [] }], ['link'], ['clean']
        ],
    };

    useEffect(() => {
        const baseLangCode = dictationLang.split('-')[0].toLowerCase();
        if (COMMANDS[baseLangCode]) setCommandTab(baseLangCode);
        else setCommandTab('en');
    }, [dictationLang]);

    useEffect(() => {
        const button = micButtonRef.current;
        if (!button) return;

        const textSpan = button.querySelector('#button-text-span');
        const icon = button.querySelector('i.fas');

        if (isVerifyingToken) {
            if (textSpan) textSpan.textContent = 'Verifying...';
            button.classList.remove(styles.actionButtonListening);
        } else if (listening) {
            if (textSpan) textSpan.textContent = 'Stop Listening';
            if (icon) icon.className = 'fas fa-microphone-alt-slash mr-1';
            button.classList.add(styles.actionButtonListening);
        } else {
            if (textSpan) textSpan.textContent = 'Start Listening';
            if (icon) icon.className = 'fas fa-microphone-alt mr-1';
            button.classList.remove(styles.actionButtonListening);
        }
    }, [listening, isVerifyingToken]);


    const handleDictationLangChange = (event) => {
        const newLang = event.target.value;
        setDictationLang(newLang);
        if (listening) {
            stopRecognition();
        }
    };

    const handleMicClick = async () => {
        clearAlerts();
        if (!browserSupportsSpeechRecognition) { showError("Browser doesn't support speech recognition."); return; }
        if (!isMicrophoneAvailable) { showError("Microphone access denied or unavailable. Please allow access."); return; }
        if (!recognitionRef.current) { showError("Speech recognition is not ready. Please try refreshing."); return; }

        if (!listening) {
            setIsVerifyingToken(true);
            try {
                await getUserProfile();
                setListening(true);
                startRecognition();
            } catch (authError) {
                console.error("handleMicClick: Session verification failed or start error:", authError);
                const errorMessage = authError.response?.data?.message || authError.message || "Session error.";
                showError(`Failed to start: ${errorMessage}`);
                if (authError.response?.status === 401 || authError.response?.status === 403) {
                    logoutUser(); navigate('/user/login');
                }
            } finally {
                setIsVerifyingToken(false);
            }
        } else {
            setListening(false);
            stopRecognition();
        }
    };

    const handleLogout = useCallback(() => {
        clearAlerts();
        logoutUser();
        navigate('/user/login');
    }, [logoutUser, navigate, clearAlerts]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    }, [isSidebarCollapsed]);

    const showCommandsModal = useCallback(() => { clearAlerts(); setIsModalVisible(true); }, [clearAlerts]);
    const hideCommandsModal = useCallback(() => setIsModalVisible(false), []);
    const selectCommandTab = useCallback((tab) => setCommandTab(tab), []);

    const handleCategorySelect = useCallback((category) => {
        clearAlerts();
        setSelectedCategory(category);
        setDraftModalVisible(false); // Close the categories modal
        setCategoryDetailsModalVisible(true); // Open the details modal
    }, [clearAlerts, setDraftModalVisible]);

    const handleCategoryDetailsSubmit = useCallback((category, formData, additionalDetails, template) => {
        clearAlerts();
        setCategoryDetailsModalVisible(false);
        // Use the enhanced handleConfirmGenerateDraft with template support
        handleConfirmGenerateDraft(category, formData, additionalDetails, template);
    }, [clearAlerts, handleConfirmGenerateDraft]);

    const hasEditorText = (quillRef.current?.getEditor().getLength() > 1) || (editorHtml && editorHtml.trim() !== '' && editorHtml !== '<p><br></p>');

    return (
        <div className={`${styles.pageWrapper} ${styles.converterPageWrapper}`}>
            {/* Top Navbar - Full Width */}
            <header className={styles.topNavbar}>
                <div className={styles.navbarLeft}>
                    <Link to="/user/editor" className={styles.navbarBrand}><img src="/icon.png" alt="Daily Speaking Logo" style={{ height: '50px' }} /></Link>
                    <button onClick={toggleSidebar} className={styles.sidebarToggleButton} title={isSidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
                        <i className={`fas ${isSidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'} mr-1`}></i>
                        {isSidebarCollapsed ? 'Show' : 'Hide'}
                    </button>
                </div>
                <div className={styles.navbarInfo}>
                    <span title={actualExpiryDate || ''}><i className="far fa-calendar-alt mr-1"></i> License: {expiryDisplayText}</span>
                    <span><i className="fas fa-phone-alt mr-1"></i> Enquiry: +91-7678073260</span>
                    <button onClick={handleLogout} className={styles.logoutButton}>
                        <i className="fas fa-sign-out-alt mr-1"></i> Logout
                    </button>
                </div>
            </header>

            {/* Alerts - Full Width */}
            <div className={styles.alertContainer}>
                {error && <div className={`${styles.alert} ${styles.alertDanger}`}>{error} <button onClick={clearAlerts} className={styles.alertClose}>×</button></div>}
                {successMessage && <div className={`${styles.alert} ${styles.alertSuccess}`}>{successMessage} <button onClick={clearAlerts} className={styles.alertClose}>×</button></div>}
            </div>

            {/* Content Area with Sidebar and Main */}
            <div className={`${styles.converterContentWrapper} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
                {/* Left Sidebar */}
                <aside className={`${styles.converterSidebar} ${isSidebarCollapsed ? styles.sidebarHidden : ''}`}>
                    <div className={`${styles.sidebarCard} ${styles.sidebarCardNarrow} ${styles.speechInputCard}`}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.sidebarTitle}><i className="fas fa-microphone-alt mr-2"></i> Speech Input</h3>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.controlGroup}>
                                <select id="dictationLang" className={`${styles.formControlSm} ${styles.formControlNarrow}`} value={dictationLang} onChange={handleDictationLangChange} disabled={listening || isVerifyingToken}>
                                    {dictationLanguages.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
                                </select>
                                {!browserSupportsSpeechRecognition && (<p className={styles.micWarning}>Speech recognition not supported.</p>)}
                                {browserSupportsSpeechRecognition && !isMicrophoneAvailable && (<p className={styles.micWarning}>Microphone access needed.</p>)}
                            </div>
                            <button
                                ref={micButtonRef}
                                onClick={handleMicClick}
                                className={`${styles.actionButton} ${styles.actionButtonPrimary} ${styles.btnBlock} ${listening && !isVerifyingToken ? styles.actionButtonListening : ''}`}
                                disabled={!browserSupportsSpeechRecognition || !isMicrophoneAvailable || !recognitionRef.current || (isVerifyingToken && listening)}
                                aria-pressed={listening && !isVerifyingToken}
                                title={listening && !isVerifyingToken ? "Stop speech recognition" : "Start speech recognition"}
                                id="speech-recognition-button"
                            >
                                {isVerifyingToken ? (
                                    <LoadingSpinner small centered={false} />
                                ) : (
                                    <i className={`fas ${listening ? 'fa-microphone-alt-slash' : 'fa-microphone-alt'} mr-1`}></i>
                                )}
                                <span id="button-text-span">
                                    {isVerifyingToken ? 'Verifying...' : listening ? 'Stop Listening' : 'Start Listening'}
                                </span>
                                {listening && !isVerifyingToken && <span className={`spinner-border spinner-border-sm ml-2 ${styles.listeningSpinner}`} role="status" aria-hidden="true"></span>}
                            </button>
                            {listening && !isVerifyingToken && (
                                <div id="listening-indicator" style={{ marginTop: '10px', fontSize: '0.85rem', color: '#4caf50' }}>
                                    <i className="fas fa-wave-square mr-1"></i> Listening...
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`${styles.sidebarCard} ${styles.sidebarCardNarrow} ${styles.translationCard}`}>
                        <div className={styles.cardHeader}> <h3 className={styles.sidebarTitle}><i className="fas fa-language mr-2"></i> Translation</h3> </div>
                        <div className={styles.cardBody}>
                            <div className={styles.controlGroup}>
                                <select id="translateLang" className={`${styles.formControlSm} ${styles.formControlNarrow}`} value={translateLang} onChange={(e) => setTranslateLang(e.target.value)} disabled={isTranslating}>
                                    {translationLanguages.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
                                </select>
                            </div>
                            <button onClick={() => handleTranslate(translateLang)} className={`${styles.actionButton} ${styles.actionButtonSecondary} ${styles.btnBlock}`} disabled={isTranslating || !hasEditorText} title="Translate">
                                {isTranslating ? <LoadingSpinner small centered={false} /> : <i className="fas fa-exchange-alt"></i>}
                            </button>
                        </div>
                    </div>

                    <div className={`${styles.sidebarCard} ${styles.sidebarCardNarrow} ${styles.transliterationCard}`}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.sidebarTitle}><i className="fas fa-keyboard mr-2"></i> Transliteration</h3>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.controlGroup}>
                                <select
                                    id="transliterationLang"
                                    className={`${styles.formControlSm} ${styles.formControlNarrow}`}
                                    value={transliterationLang}
                                    onChange={(e) => setTransliterationLang(e.target.value)}
                                    disabled={isTransliterating}
                                >
                                    {transliterationLanguages.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
                                </select>
                            </div>
                            <button
                                onClick={() => setIsTransliterationEnabled(!isTransliterationEnabled)}
                                className={`${styles.actionButton} ${isTransliterationEnabled ? styles.actionButtonDanger : styles.actionButtonSecondary} ${styles.btnBlock}`}
                                disabled={isTransliterating}
                            >
                                {isTransliterating ? <LoadingSpinner small centered={false} /> : (isTransliterationEnabled ? 'Disable' : 'Enable')}
                            </button>
                        </div>
                    </div>

                    <div className={styles.sidebarCard}>
                        <div className={styles.cardHeader}> <h3 className={styles.sidebarTitle}><i className="fas fa-font mr-2"></i> Font Conversion</h3> </div>
                        <div className={styles.cardBody}>
                            <div className={styles.controlGroup}>
                                <select
                                    id="fontSelect"
                                    className={styles.formControlSm}
                                    disabled={isConvertingFont || !hasEditorText}
                                >
                                    <option value="kruti">Krutidev</option>
                                    <option value="preeti">Preeti</option>
                                    <option value="shree">Shreelipi</option>
                                    <option value="shivaji">Shivaji</option>
                                    <option value="aps">APS</option>
                                    <option value="ism">ISM</option>
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    const select = document.getElementById('fontSelect');
                                    if (select) handleFontConversion(select.value);
                                }}
                                className={`${styles.actionButton} ${styles.actionButtonSecondary} ${styles.btnBlock}`}
                                disabled={isConvertingFont || !hasEditorText}
                                title="Convert to selected font"
                            >
                                {isConvertingFont ? <LoadingSpinner small centered={false} /> : <i className="fas fa-exchange-alt"></i>}
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Content Area */}
                <main className={styles.converterMainContent}>
                    <div className={styles.actionsToolbar}>
                        <div className={styles.actionsToolbarContent}>
                            <button onClick={() => navigate('/user/content-history')} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} title="My Docs">
                                <i className="fas fa-history"></i>
                            </button>
                            <button onClick={() => navigate('/user/chat-with-ai')} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} title="AI Chat">
                                <i className="fas fa-comments"></i>
                            </button>
                            <button onClick={() => navigate('/user/converter')} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} title="Unicode Converter">
                                <i className="fas fa-font"></i>
                            </button>
                            <button onClick={handleCopy} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} disabled={!hasEditorText} title="Copy"><i className="fas fa-copy"></i></button>
                            <button onClick={handlePrint} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} disabled={!hasEditorText} title="Print"><i className="fas fa-print"></i></button>
                            <button onClick={handleFixGrammar} className={`${styles.actionButton} ${styles.actionButtonHighlight}`} disabled={isFixingGrammar || !hasEditorText} title="Fix Grammar">
                                {isFixingGrammar ? <LoadingSpinner small centered={false} /> : <i className="fas fa-check-double"></i>}
                            </button>
                            <button onClick={handleRefineTone} className={`${styles.actionButton} ${styles.actionButtonHighlight}`} disabled={isRefiningTone || !hasEditorText} title="Refine Tone">
                                {isRefiningTone ? <LoadingSpinner small centered={false} /> : <i className="fas fa-magic"></i>}
                            </button>
                            <button onClick={handleExpandContent} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} disabled={isExpandingContent || !hasEditorText} title="AI Expansion">
                                {isExpandingContent ? <LoadingSpinner small centered={false} /> : <i className="fas fa-expand-alt"></i>}
                            </button>
                            <button onClick={handleGenerateDraft} className={`${styles.actionButton} ${styles.actionButtonSecondary}`} disabled={isGeneratingDraft} title="Generate Draft">
                                {isGeneratingDraft ? <LoadingSpinner small centered={false} /> : <i className="fas fa-file-alt"></i>}
                            </button>
                            <button onClick={showCommandsModal} className={`${styles.actionButton} ${styles.actionButtonInfo}`} title="Commands"><i className="fas fa-terminal"></i></button>
                            <button onClick={handleClear} className={`${styles.actionButton} ${styles.actionButtonDanger}`} disabled={!hasEditorText || listening} title="Clear"><i className="fas fa-trash"></i></button>
                            <button
                                onClick={() => setAudioUploadModalVisible(true)}
                                className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                                disabled={isConvertingAudio}
                                title="Audio to Text"
                            >
                                {isConvertingAudio ? <LoadingSpinner small centered={false} /> : <i className="fas fa-file-audio"></i>}
                            </button>
                            <button
                                onClick={() => setImageUploadModalVisible(true)}
                                className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                                disabled={isConvertingImage}
                                title="Image to Text"
                            >
                                {isConvertingImage ? <LoadingSpinner small centered={false} /> : <i className="fas fa-file-image"></i>}
                            </button>
                            <button
                                onClick={() => setPdfUploadModalVisible(true)}
                                className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                                disabled={isConvertingPdf}
                                title="PDF to Text"
                            >
                                {isConvertingPdf ? <LoadingSpinner small centered={false} /> : <i className="fas fa-file-pdf"></i>}
                            </button>
                            <button
                                onClick={handleSave}
                                className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                                disabled={isSavingContent || !hasEditorText}
                                title="Save Content"
                            >
                                {isSavingContent ? <LoadingSpinner small centered={false} /> : <i className="fas fa-save"></i>}
                            </button>
                        </div>
                    </div>

                    <div className={styles.editorWrapper}>
                        <InterimResultsDisplay
                            interimText={interimText}
                            isListening={listening}
                            position="compact"
                        />
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={editorHtml}
                            onChange={handleEditorChange}
                            modules={quillModules}
                            placeholder="Start speaking or typing here..."
                            className={`${styles.quillEditor} ${isFixingGrammar ? styles.grammarFixAnimation : ''}`}
                        />
                    </div>
                </main>
            </div>

            <SaveContentModal
                isVisible={saveModalVisible}
                onClose={() => setSaveModalVisible(false)}
                contentTitle={contentTitle}
                setContentTitle={setContentTitle}
                contentDescription={contentDescription}
                setContentDescription={setContentDescription}
                onConfirmSave={handleConfirmSave}
                isSavingContent={isSavingContent}
            />

            <CommandsModal
                isVisible={isModalVisible}
                onClose={hideCommandsModal}
                commandTab={commandTab}
                selectCommandTab={selectCommandTab}
            />

            <DraftCategoriesModal
                isVisible={draftModalVisible}
                onClose={() => setDraftModalVisible(false)}
                onCategorySelect={handleCategorySelect}
            />

            <CategoryDetailsModal
                isVisible={categoryDetailsModalVisible}
                onClose={() => setCategoryDetailsModalVisible(false)}
                category={selectedCategory}
                onSubmit={handleCategoryDetailsSubmit}
            />

            <TranslationModal
                isVisible={translationModalVisible}
                onClose={() => setTranslationModalVisible(false)}
                translatedText={translatedText}
                onCopyAndClose={() => {
                    navigator.clipboard.writeText(translatedText)
                        .then(() => showSuccess("Translation copied!"))
                        .catch(() => showError("Copy failed."));
                    setTranslationModalVisible(false);
                }}
            />

            <FontConversionModal
                isVisible={fontConversionModalVisible}
                onClose={() => setFontConversionModalVisible(false)}
                selectedFont={selectedFont}
                conversionOutput={conversionOutput}
                onCopyAndClose={() => {
                    navigator.clipboard.writeText(conversionOutput)
                        .then(() => showSuccess("Conversion output copied!"))
                        .catch(() => showError("Copy failed."));
                    setFontConversionModalVisible(false);
                }}
            />

            <LoadingModal isVisible={isLoadingModalVisible} message={loadingMessage} />
            <LoadingModal isVisible={fileLoadingModalVisible} message={fileLoadingMessage} />

            <AudioUploadModal
                isVisible={audioUploadModalVisible}
                onClose={() => setAudioUploadModalVisible(false)}
                onConvert={handleAudioToText}
                isConverting={isConvertingAudio}
            />

            <ImageUploadModal
                isVisible={imageUploadModalVisible}
                onClose={() => setImageUploadModalVisible(false)}
                onConvert={handleImageToText}
                isConverting={isConvertingImage}
            />

            <PdfUploadModal
                isVisible={pdfUploadModalVisible}
                onClose={() => setPdfUploadModalVisible(false)}
                onConvert={handlePdfToText}
                isConverting={isConvertingPdf}
            />

            <ConversionResultModal
                isVisible={resultModalVisible}
                onClose={() => setResultModalVisible(false)}
                title={activeConversionResult.title}
                icon={activeConversionResult.icon}
                convertedText={convertedText}
                onInsertText={handleInsertText}
                onCopyText={handleCopyText}
            />
        </div>
    );
}

export default ModernSpeechEditor;
